import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { createAnimationChannel } from "./animation/animationChannel";
import { ConfirmDialog } from "./components/ConfirmDialog/ConfirmDialog";
import type { GameBoardPhase } from "./components/GameBoard/GameBoard";
import { MatchBoardSurface } from "./components/MatchBoardSurface/MatchBoardSurface";
import { LiveAnnouncements } from "./components/LiveAnnouncements/LiveAnnouncements";
import {
  createSharedMatchUrl,
  HistoryReplay,
  HistoryScreen,
  parseSharedMatchHash,
  type ShareResult,
  type SharedMatchParseResult,
} from "./features/history";
import type { MatchControllerSnapshot } from "./features/match/controller";
import { MatchMenu } from "./features/match/menu";
import { MatchResult } from "./features/result";
import { RulesGuide } from "./features/rules";
import { ContinueMatchDialog, MatchSetup, type MatchSetupSelection } from "./features/setup";
import { SettingsPanel } from "./features/settings";
import type { GameState, Move, Player, TransitionEvent } from "./game";
import { createMatchRecord, type MatchRecordV1 } from "./features/match/persistence";
import type { SoundFeedbackPort } from "./platform/feedback";
import { createBrowserAppRuntime, type AppRuntime } from "./platform/runtime/browserAppRuntime";
import {
  DEFAULT_SETTINGS,
  SETTINGS_STORAGE_KEY,
  type Settings,
} from "./platform/settings/settingsStore";

type AppView =
  | "match"
  | "menu"
  | "rules"
  | "settings"
  | "result"
  | "review"
  | "history"
  | "history-replay"
  | "shared-error";
type Confirmation = "new-game" | null;
type ContinuationRequest = Readonly<{
  game: GameState;
  selection: MatchSetupSelection;
  replacingActiveMatch: boolean;
}>;

export type AppProps = Readonly<{ runtime?: AppRuntime }>;

export function App({ runtime: suppliedRuntime }: AppProps) {
  const [sharedMatch, setSharedMatch] = useState<SharedMatchParseResult>(() =>
    parseSharedMatchHash(globalThis.location.hash),
  );
  const [runtime] = useState(() => suppliedRuntime ?? createBrowserAppRuntime());
  const snapshot = useSyncExternalStore(
    runtime.controller.subscribe,
    runtime.controller.getSnapshot,
    runtime.controller.getSnapshot,
  );
  const activeMatchAnimation = useSyncExternalStore(
    runtime.animation.subscribe,
    runtime.animation.getSnapshot,
    runtime.animation.getSnapshot,
  );
  const [reviewAnimation] = useState(createAnimationChannel);
  const activeReviewAnimation = useSyncExternalStore(
    reviewAnimation.subscribe,
    reviewAnimation.getSnapshot,
    reviewAnimation.getSnapshot,
  );
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [view, setView] = useState<AppView>(() =>
    sharedMatch.kind === "valid"
      ? "history-replay"
      : sharedMatch.kind === "invalid"
        ? "shared-error"
        : "match",
  );
  const [menuReturnView, setMenuReturnView] = useState<AppView>("match");
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [continuation, setContinuation] = useState<ContinuationRequest | null>(null);
  const [continuationBusy, setContinuationBusy] = useState(false);
  const [continuationError, setContinuationError] = useState<string | null>(null);
  const returnView = useRef<AppView>("match");
  const boardHeading = useRef<HTMLHeadingElement>(null);
  const menuSurface = useRef<HTMLDivElement>(null);
  const resultSurface = useRef<HTMLDivElement>(null);
  const setupSurface = useRef<HTMLDivElement>(null);
  const reviewSurface = useRef<HTMLDivElement>(null);
  const appliedFocusRequest = useRef<string | null>(null);
  const newGameButton = useRef<HTMLButtonElement>(null);
  const [reviewStep, setReviewStep] = useState(0);
  const [reviewShareResult, setReviewShareResult] = useState<ShareResult | null>(null);
  const [historyReturnView, setHistoryReturnView] = useState<AppView>("match");
  const [historyRecord, setHistoryRecord] = useState<MatchRecordV1 | null>(() =>
    sharedMatch.kind === "valid" ? sharedMatch.record : null,
  );
  const [historyReplayReturnView, setHistoryReplayReturnView] = useState<
    "history" | "result" | "shared"
  >(() => (sharedMatch.kind === "valid" ? "shared" : "history"));
  const restoreHistoryInvokerFocus = useRef(false);
  const suppressPresentationFocus = useRef(false);
  const sharedRouteActive = useRef(sharedMatch.kind !== "none");

  const leaveSharedRoute = useCallback(
    (clearHash: boolean): void => {
      reviewAnimation.port.cancel();
      sharedRouteActive.current = false;
      runtime.controller.setHidden(document.hidden);
      if (clearHash) clearSharedMatchHash();
      setSharedMatch(Object.freeze({ kind: "none" }));
      setHistoryRecord(null);
      setView("match");
    },
    [reviewAnimation, runtime.controller],
  );

  useEffect(() => () => reviewAnimation.port.cancel(), [reviewAnimation]);

  useEffect(() => {
    if (sharedRouteActive.current) runtime.controller.setHidden(true);
    const openSharedHash = (): void => {
      const parsed = parseSharedMatchHash(globalThis.location.hash);
      if (parsed.kind === "none") {
        if (sharedRouteActive.current) leaveSharedRoute(false);
        return;
      }
      sharedRouteActive.current = true;
      runtime.controller.setHidden(true);
      reviewAnimation.port.cancel();
      setSharedMatch(parsed);
      if (parsed.kind === "valid") {
        setHistoryRecord(parsed.record);
        setHistoryReplayReturnView("shared");
        setView("history-replay");
      } else {
        setHistoryRecord(null);
        setView("shared-error");
      }
    };
    globalThis.addEventListener("hashchange", openSharedHash);
    return () => globalThis.removeEventListener("hashchange", openSharedHash);
  }, [leaveSharedRoute, reviewAnimation, runtime.controller]);

  useEffect(() => {
    void runtime.controller.restore();
    void runtime.settingsStore.load().then(({ settings: loaded }) => {
      setSettings(loaded);
      runtime.sound.setEnabled(loaded.soundEnabled);
      runtime.haptics.setEnabled(loaded.hapticsEnabled);
    });
    const unsubscribeLifecycle = runtime.lifecycle.subscribe((event) => {
      if (event === "resize") runtime.animation.port.cancel();
      else if (event === "suspend") runtime.controller.setHidden(true);
      else {
        runtime.controller.setHidden(sharedRouteActive.current);
        if (!sharedRouteActive.current) void runtime.controller.takeOver();
      }
    });
    const syncSettings = (event: StorageEvent): void => {
      if (event.key !== SETTINGS_STORAGE_KEY) return;
      void runtime.settingsStore.load().then(({ settings: loaded }) => {
        setSettings(loaded);
        runtime.sound.setEnabled(loaded.soundEnabled);
        runtime.haptics.setEnabled(loaded.hapticsEnabled);
      });
    };
    globalThis.addEventListener("storage", syncSettings);
    return () => {
      unsubscribeLifecycle();
      globalThis.removeEventListener("storage", syncSettings);
    };
  }, [runtime]);

  useEffect(() => {
    if (snapshot.persistence.kind === "conflict" && !document.hidden && document.hasFocus()) {
      void runtime.controller.takeOver();
    }
  }, [runtime.controller, snapshot.persistence.kind]);

  const effectiveView: AppView =
    snapshot.phase === "finished" && view === "match"
      ? "result"
      : snapshot.phase === "setup" && (view === "result" || view === "review")
        ? "match"
        : view;

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [effectiveView]);

  const feedback = useMemo(
    () => ({
      emit(cue: Parameters<SoundFeedbackPort["emit"]>[0]) {
        runtime.sound.emit(cue);
        runtime.haptics.emit(cue);
      },
    }),
    [runtime],
  );
  const activeAnimation = activeReviewAnimation ?? activeMatchAnimation;
  const finishAnimation = useCallback(
    (id: number) => {
      if (activeReviewAnimation !== null) reviewAnimation.finish(id);
      else runtime.animation.finish(id);
    },
    [activeReviewAnimation, reviewAnimation, runtime.animation],
  );

  const openView = (next: "rules" | "settings"): void => {
    returnView.current = effectiveView === "menu" ? "menu" : "match";
    setView(next);
  };
  const closeSubView = (): void => setView(returnView.current);
  const applySettings = async (next: Settings): Promise<boolean> => {
    setSettings(next);
    runtime.sound.setEnabled(next.soundEnabled);
    runtime.haptics.setEnabled(next.hapticsEnabled);
    return (await runtime.settingsStore.save(next)).kind === "saved";
  };
  const openMenu = (): void => {
    setMenuReturnView(effectiveView);
    if (snapshot.phase === "finished" || runtime.controller.pause("user").ok) setView("menu");
  };
  const openHistory = (): void => {
    setHistoryReturnView(effectiveView);
    setHistoryRecord(null);
    setView("history");
  };
  const closeHistory = (): void => {
    setHistoryRecord(null);
    restoreHistoryInvokerFocus.current = true;
    setView(historyReturnView);
  };
  const resume = (): void => {
    if (snapshot.phase === "finished") {
      setView(menuReturnView === "review" ? "review" : "result");
      return;
    }
    runtime.controller.resume();
    setView("match");
  };
  const handleMove = (move: Move): void => {
    void runtime.sound.activate();
    void runtime.controller.playHumanMove(move);
  };
  const confirmAction = (): void => {
    setConfirmation(null);
    void runtime.controller.restart();
    setView("match");
  };
  const openContinuation = (game: GameState, selection: MatchSetupSelection): void => {
    setContinuationError(null);
    setContinuationBusy(false);
    setContinuation(
      Object.freeze({
        game,
        selection,
        replacingActiveMatch: snapshot.phase !== "setup" && snapshot.phase !== "finished",
      }),
    );
  };
  const startContinuation = async (selection: MatchSetupSelection): Promise<void> => {
    if (continuation === null || continuationBusy) return;
    setContinuationBusy(true);
    setContinuationError(null);
    try {
      const result = await runtime.controller.continueFrom(continuation.game, selection);
      if (result.ok) {
        if (historyReplayReturnView === "shared") leaveSharedRoute(true);
        else {
          reviewAnimation.port.cancel();
          setHistoryRecord(null);
          setView("match");
        }
        setContinuation(null);
      } else {
        setContinuationError(continuationErrorMessage(result.reason));
      }
    } catch {
      setContinuationError(
        "The new match could not be started. Your current match was left unchanged.",
      );
    } finally {
      setContinuationBusy(false);
    }
  };

  const review = snapshot.review;
  const reviewMoveCount = review?.transitions.length ?? 0;
  const displayedReviewStep = Math.min(reviewStep, reviewMoveCount);
  const reviewGame = review?.states[displayedReviewStep] ?? snapshot.game;
  const boardGame = activeAnimation?.request.previousGame ?? reviewGame;
  const showSetup = snapshot.phase === "setup" && effectiveView === "match";
  const showBoard =
    boardGame !== null &&
    !["rules", "settings", "history", "history-replay", "shared-error"].includes(effectiveView);
  const announcement = announcementText(snapshot);
  const reviewExitedDisc =
    effectiveView === "review" &&
    activeReviewAnimation === null &&
    snapshot.result?.kind === "passage" &&
    review !== null &&
    displayedReviewStep === reviewMoveCount
      ? exitedReviewDisc(review)
      : null;

  const openReview = (): void => {
    reviewAnimation.port.cancel();
    setReviewShareResult(null);
    if (review === null && snapshot.historyId !== null) {
      void runtime.history.load(snapshot.historyId).then((loaded) => {
        if (loaded.kind !== "loaded" || loaded.value === null) return;
        setHistoryReturnView("result");
        setHistoryReplayReturnView("result");
        setHistoryRecord(loaded.value);
        setView("history-replay");
      });
      return;
    }
    setReviewStep(reviewMoveCount);
    setView("review");
  };
  const stepReview = (direction: "backward" | "forward"): void => {
    if (review === null || activeReviewAnimation !== null) return;
    const edgeIndex = direction === "forward" ? displayedReviewStep : displayedReviewStep - 1;
    const transition = review.transitions[edgeIndex];
    if (transition === undefined) return;
    setReviewShareResult(null);
    setReviewStep((current) => current + (direction === "forward" ? 1 : -1));
    void reviewAnimation.port.play({
      ...(direction === "forward" ? transition.forward : transition.reverse),
      timing: "review",
    });
  };
  const jumpReview = (target: 0 | "end"): void => {
    if (activeReviewAnimation !== null) return;
    setReviewShareResult(null);
    setReviewStep(target === 0 ? 0 : reviewMoveCount);
  };
  const currentMatchRecordForShare = async (): Promise<MatchRecordV1 | null> => {
    if (snapshot.historyId === null) return null;
    const loaded = await runtime.history.load(snapshot.historyId);
    return loaded.kind === "loaded" && loaded.value !== null
      ? loaded.value
      : currentReviewRecord(snapshot);
  };
  const shareCurrentReview = async (): Promise<void> => {
    setReviewShareResult(null);
    if (snapshot.historyId === null) {
      setReviewShareResult(
        Object.freeze({ kind: "error", message: "This match is not available to share." }),
      );
      return;
    }
    const record = await currentMatchRecordForShare();
    if (record === null) {
      setReviewShareResult(
        Object.freeze({ kind: "error", message: "This match could not be prepared for sharing." }),
      );
      return;
    }
    setReviewShareResult(await shareMatch(record, displayedReviewStep));
  };
  const shareFinishedMatch = async (): Promise<ShareResult> => {
    const record = await currentMatchRecordForShare();
    return record === null
      ? Object.freeze({
          kind: "error",
          message: "This match could not be prepared for sharing.",
        })
      : shareMatch(record, record.moves.length);
  };

  useEffect(() => {
    appliedFocusRequest.current = null;
    if (effectiveView === "menu") {
      menuSurface.current?.querySelector<HTMLElement>("button")?.focus();
    } else if (effectiveView === "review") {
      reviewSurface.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
    }
  }, [effectiveView]);

  useEffect(() => {
    if (!restoreHistoryInvokerFocus.current || effectiveView !== historyReturnView) return;
    const invoker = document.querySelector<HTMLElement>("[data-history-action]");
    if (invoker === null) return;
    restoreHistoryInvokerFocus.current = false;
    suppressPresentationFocus.current = true;
    invoker.focus();
  }, [effectiveView, historyReturnView]);

  useEffect(() => {
    if (suppressPresentationFocus.current) {
      suppressPresentationFocus.current = false;
      return;
    }
    const target = snapshot.presentation.focusTarget;
    if (target === null || activeAnimation !== null) return;
    const version = snapshot.authority;
    const key = `${version?.epoch ?? "none"}:${version?.revision ?? -1}:${target.kind}`;
    if (appliedFocusRequest.current === key) return;

    let element: HTMLElement | null = null;
    if (target.kind === "board-heading" && effectiveView === "match") {
      element = boardHeading.current;
    } else if (target.kind === "result-primary" && effectiveView === "result") {
      element = resultSurface.current?.querySelector("button") ?? null;
    } else if (target.kind === "setup-primary" && showSetup) {
      element = setupSurface.current?.querySelector("button[type=submit]") ?? null;
    }
    if (element === null) return;
    appliedFocusRequest.current = key;
    element.focus();
  }, [
    activeAnimation,
    effectiveView,
    showSetup,
    snapshot.authority,
    snapshot.presentation.focusTarget,
  ]);

  return (
    <main className="app-shell" data-view={effectiveView}>
      <LiveAnnouncements
        botStatus={snapshot.phase === "bot-thinking" ? "The bot is thinking." : ""}
        terminalSummary={announcement.terminal}
        turnSummary={announcement.turn}
      />

      <header className="app-header">
        <span aria-label="Passo" className="app-wordmark">
          Passo
        </span>
        <div className="app-header__actions">
          {snapshot.game !== null &&
          (effectiveView === "match" ||
            effectiveView === "result" ||
            effectiveView === "review") ? (
            <>
              {snapshot.phase !== "finished" && effectiveView === "match" ? (
                <button
                  type="button"
                  className="app-header__icon-button"
                  aria-label="Undo"
                  onClick={() => {
                    void runtime.sound.activate();
                    void runtime.controller.undoLastMove();
                  }}
                  disabled={
                    !snapshot.canUndo ||
                    snapshot.ownership.kind !== "owner" ||
                    snapshot.persistence.kind === "conflict" ||
                    snapshot.phase === "reconciling"
                  }
                >
                  <span
                    aria-hidden="true"
                    className="app-header__icon-symbol app-header__icon-symbol--undo"
                  >
                    ↺
                  </span>
                </button>
              ) : null}
              <button
                type="button"
                className="app-header__icon-button"
                aria-label="Menu"
                onClick={openMenu}
                disabled={snapshot.phase !== "finished" && snapshot.ownership.kind !== "owner"}
              >
                <span
                  aria-hidden="true"
                  className="app-header__icon-symbol app-header__icon-symbol--menu"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="3" x2="17" y1="6" y2="6" />
                    <line x1="3" x2="17" y1="10" y2="10" />
                    <line x1="3" x2="17" y1="14" y2="14" />
                  </svg>
                </span>
              </button>
            </>
          ) : null}
          {snapshot.phase === "setup" ? (
            <>
              <button type="button" data-history-action onClick={openHistory}>
                History
              </button>
              <button type="button" onClick={() => openView("rules")}>
                Rules
              </button>
              <button type="button" onClick={() => openView("settings")}>
                Settings
              </button>
            </>
          ) : null}
        </div>
      </header>

      {snapshot.phase === "restoring" ? <LoadingTable /> : null}

      {showSetup ? (
        <div className="setup-layout" ref={setupSurface}>
          <section className="setup-introduction" aria-labelledby="passo-title">
            <h1 id="passo-title">Passo</h1>
          </section>
          <MatchSetup
            discMarkers={settings.discMarkersEnabled}
            initialSelection={snapshot.setupSelection}
            onStart={(selection) => {
              void runtime.sound.activate();
              void runtime.controller.start(selection);
            }}
          />
        </div>
      ) : null}

      {effectiveView === "rules" ? (
        <div className="surface-card">
          <RulesGuide onClose={closeSubView} />
        </div>
      ) : null}
      {effectiveView === "settings" ? (
        <div className="surface-card">
          <SettingsPanel
            capabilities={{ sound: runtime.sound.available, haptics: runtime.haptics.available }}
            onChange={applySettings}
            onClose={closeSubView}
            settings={settings}
          />
        </div>
      ) : null}
      {effectiveView === "history" ? (
        <HistoryScreen
          history={runtime.history}
          {...(historyRecord === null ? {} : { initialFocusId: historyRecord.id })}
          onBack={closeHistory}
          onOpen={(record) => {
            setHistoryReplayReturnView("history");
            setHistoryRecord(record);
            setView("history-replay");
          }}
        />
      ) : null}
      {effectiveView === "history-replay" && historyRecord !== null ? (
        <HistoryReplay
          record={historyRecord}
          discMarkers={settings.discMarkersEnabled}
          motion={settings.motion}
          backLabel={
            historyReplayReturnView === "result"
              ? "Back to result"
              : historyReplayReturnView === "shared"
                ? "Back to Passo"
                : "Back to history"
          }
          {...(historyReplayReturnView === "shared" && sharedMatch.kind === "valid"
            ? { initialStep: sharedMatch.step, highlightedStep: sharedMatch.step }
            : {})}
          onBack={() => {
            if (historyReplayReturnView === "result") {
              restoreHistoryInvokerFocus.current = true;
              setView("result");
            } else if (historyReplayReturnView === "shared") {
              leaveSharedRoute(true);
            } else setView("history");
          }}
          onContinue={openContinuation}
          onShare={(step) => shareMatch(historyRecord, step)}
          sourceLabel={historyReplayReturnView === "shared" ? "Shared match" : "Saved match"}
        />
      ) : null}
      {effectiveView === "shared-error" && sharedMatch.kind === "invalid" ? (
        <section className="surface-card" aria-labelledby="shared-match-error-title">
          <h1 id="shared-match-error-title">Shared match unavailable</h1>
          <p role="alert">{sharedMatch.message}</p>
          <button type="button" onClick={() => leaveSharedRoute(true)}>
            Back to Passo
          </button>
        </section>
      ) : null}

      {showBoard ? (
        <div className="match-layout">
          <section className="match-status" aria-labelledby="match-status-title">
            <h1
              className={
                snapshot.phase === "bot-thinking" ? "match-status__title--thinking" : undefined
              }
              data-motion={settings.motion}
              id="match-status-title"
              ref={boardHeading}
              tabIndex={-1}
            >
              <span>{statusTitle(snapshot)}</span>
            </h1>
          </section>
          <MatchBoardSurface
            activeAnimation={activeAnimation}
            animationFeedback={feedback}
            discMarkers={settings.discMarkersEnabled}
            disabled={snapshot.phase !== "human-turn" || effectiveView !== "match"}
            focusRecoveryTarget={focusPosition(snapshot)}
            legalMoves={snapshot.legalMoves}
            onCancelSelection={() => {
              runtime.controller.clearSelection();
            }}
            onMove={handleMove}
            onFocusRecoveryUnavailable={() => {
              if (snapshot.phase === "finished") {
                resultSurface.current?.querySelector<HTMLElement>("button")?.focus();
              } else {
                boardHeading.current?.focus();
              }
            }}
            onSelect={(position) => {
              runtime.sound.emit("press");
              runtime.haptics.emit("press");
              runtime.controller.selectSource(position);
            }}
            phase={boardPhase(snapshot.phase)}
            selectedPosition={snapshot.selectedPosition}
            {...(effectiveView === "review" ? { "aria-label": "Passo review board" } : {})}
            reviewExitedDisc={reviewExitedDisc}
            state={boardGame}
            motion={settings.motion}
            onAnimationFinish={finishAnimation}
            viewFrom={snapshot.mode === "local" ? "black" : (snapshot.humanPlayer ?? "black")}
          />

          {effectiveView === "menu" ? (
            <div className="match-overlay" ref={menuSurface}>
              <MatchMenu
                finished={snapshot.phase === "finished"}
                finishedReturnLabel={
                  menuReturnView === "review" ? "Return to review" : "Return to result"
                }
                onOpenRules={() => openView("rules")}
                onOpenSettings={() => openView("settings")}
                onOpenHistory={openHistory}
                onRequestNewGame={() => setConfirmation("new-game")}
                onResume={resume}
                newGameButtonRef={newGameButton}
              />
            </div>
          ) : null}
          {effectiveView === "result" &&
          snapshot.result !== null &&
          snapshot.humanPlayer !== null ? (
            <div className="match-overlay match-overlay--result" ref={resultSurface}>
              <MatchResult
                humanPlayer={snapshot.humanPlayer}
                mode={snapshot.mode ?? "bot"}
                onNewGame={() => {
                  void runtime.controller.rematch();
                  setView("match");
                }}
                onReviewBoard={openReview}
                onOpenHistory={openHistory}
                onShare={shareFinishedMatch}
                outcome={snapshot.result}
              />
            </div>
          ) : null}
          {effectiveView === "review" ? (
            <div className="review-actions" ref={reviewSurface}>
              <p>{`Move ${displayedReviewStep} of ${reviewMoveCount}`}</p>
              <div aria-label="Review controls" className="review-actions__controls" role="group">
                <button
                  aria-label="Go to start"
                  className="review-actions__icon-button"
                  disabled={activeReviewAnimation !== null || displayedReviewStep === 0}
                  type="button"
                  onClick={() => jumpReview(0)}
                >
                  <ReviewIcon kind="start" />
                </button>
                <button
                  aria-label="Previous move"
                  className="review-actions__icon-button"
                  data-playing={
                    activeReviewAnimation?.request.playback?.direction === "reverse" || undefined
                  }
                  disabled={activeReviewAnimation !== null || displayedReviewStep === 0}
                  type="button"
                  onClick={() => stepReview("backward")}
                >
                  <ReviewIcon kind="previous" />
                </button>
                <button
                  aria-label="Next move"
                  className="review-actions__icon-button"
                  data-playing={
                    activeReviewAnimation?.request.playback?.direction === "forward" || undefined
                  }
                  disabled={
                    activeReviewAnimation !== null || displayedReviewStep === reviewMoveCount
                  }
                  type="button"
                  onClick={() => stepReview("forward")}
                >
                  <ReviewIcon kind="next" />
                </button>
                <button
                  aria-label="Go to end"
                  className="review-actions__icon-button"
                  disabled={
                    activeReviewAnimation !== null || displayedReviewStep === reviewMoveCount
                  }
                  type="button"
                  onClick={() => jumpReview("end")}
                >
                  <ReviewIcon kind="end" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  reviewAnimation.port.cancel();
                  setView("result");
                }}
              >
                Result
              </button>
              <button
                className="review-actions__continue"
                disabled={activeReviewAnimation !== null || reviewGame?.outcome !== null}
                title={
                  reviewGame?.outcome === null
                    ? "Start a new match from the visible position"
                    : "This position has already ended"
                }
                type="button"
                onClick={() => {
                  if (reviewGame?.outcome !== null) return;
                  openContinuation(reviewGame, {
                    mode: snapshot.mode ?? "bot",
                    humanPlayer: snapshot.humanPlayer ?? "black",
                    difficulty: snapshot.difficulty ?? "medium",
                  });
                }}
              >
                Continue from here
              </button>
              <button
                type="button"
                disabled={activeReviewAnimation !== null}
                onClick={() => void shareCurrentReview()}
              >
                Share this turn
              </button>
              {reviewShareResult === null || reviewShareResult.kind === "cancelled" ? null : (
                <div className="review-actions__share-result" aria-live="polite">
                  <p>
                    {reviewShareResult.kind === "copied"
                      ? "Link copied."
                      : reviewShareResult.kind === "shared"
                        ? "Share sheet opened."
                        : (reviewShareResult.message ?? "The link could not be shared.")}
                  </p>
                  {reviewShareResult.url === undefined ? null : (
                    <label>
                      Share link
                      <input
                        aria-label="Share link"
                        readOnly
                        value={reviewShareResult.url}
                        onFocus={(event) => event.currentTarget.select()}
                      />
                    </label>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        cancelLabel="Keep playing"
        confirmLabel="New game"
        description="This saves the current match as abandoned, then returns to setup."
        invokerRef={newGameButton}
        onCancel={() => setConfirmation(null)}
        onConfirm={confirmAction}
        open={confirmation !== null}
        title="Start a new game?"
        tone="danger"
      />
      {continuation === null ? null : (
        <ContinueMatchDialog
          busy={continuationBusy}
          discMarkers={settings.discMarkersEnabled}
          error={continuationError}
          initialSelection={continuation.selection}
          onCancel={() => {
            if (!continuationBusy) setContinuation(null);
          }}
          onContinue={(selection) => void startContinuation(selection)}
          replacingActiveMatch={continuation.replacingActiveMatch}
        />
      )}
    </main>
  );
}

function continuationErrorMessage(reason: string): string {
  switch (reason) {
    case "terminal-position":
      return "This position has already ended and cannot be continued.";
    case "incompatible-position":
      return "This historical position uses older rules and cannot start a new match.";
    case "not-owner":
      return "Another tab currently owns the active match. Return to the match and take over first.";
    default:
      return "The new match could not be started. Your current match was left unchanged.";
  }
}

async function shareMatch(record: MatchRecordV1, step: number): Promise<ShareResult> {
  let url: string;
  try {
    url = createSharedMatchUrl(record, step);
  } catch (error) {
    return Object.freeze({
      kind: "error",
      message: error instanceof Error ? error.message : "This match cannot be shared.",
    });
  }

  if (typeof globalThis.navigator.share === "function") {
    try {
      await globalThis.navigator.share({
        title: "Passo match",
        text: `Look at move ${step} of this Passo match.`,
        url,
      });
      return Object.freeze({ kind: "shared" });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return Object.freeze({ kind: "cancelled" });
      }
    }
  }

  try {
    await globalThis.navigator.clipboard.writeText(url);
    return Object.freeze({ kind: "copied" });
  } catch {
    return Object.freeze({
      kind: "error",
      message: "Copy the link below to share this turn.",
      url,
    });
  }
}

function currentReviewRecord(snapshot: MatchControllerSnapshot): MatchRecordV1 | null {
  if (
    snapshot.recording?.completedAt == null ||
    snapshot.result === null ||
    snapshot.mode === null ||
    snapshot.humanPlayer === null ||
    snapshot.historyId === null
  ) {
    return null;
  }
  return createMatchRecord({
    recording: snapshot.recording,
    mode: snapshot.mode,
    humanPlayer: snapshot.humanPlayer,
    difficulty: snapshot.difficulty ?? "medium",
    difficultyPolicyVersion: 1,
    result: snapshot.result,
  });
}

function clearSharedMatchHash(): void {
  const url = new URL(globalThis.location.href);
  url.hash = "";
  globalThis.history.replaceState(globalThis.history.state, "", url);
}

function exitedReviewDisc(review: NonNullable<MatchControllerSnapshot["review"]>) {
  const terminal = review.transitions.at(-1)?.forward;
  if (terminal?.move.kind !== "passage") return null;
  const exited = terminal.transition.events.find((event) => event.kind === "disc-exited");
  return exited?.kind === "disc-exited" ? { player: exited.player, position: exited.to } : null;
}

function ReviewIcon({ kind }: Readonly<{ kind: "start" | "previous" | "next" | "end" }>) {
  const previous = kind === "start" || kind === "previous";
  const endpoint = kind === "start" || kind === "end";
  return (
    <svg
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.9"
      viewBox="0 0 20 20"
    >
      {endpoint ? (
        <line x1={previous ? "5" : "15"} x2={previous ? "5" : "15"} y1="4" y2="16" />
      ) : null}
      <path
        d={
          previous
            ? endpoint
              ? "M14 5 8 10l6 5"
              : "M13 5 7 10l6 5"
            : endpoint
              ? "m6 5 6 5-6 5"
              : "m7 5 6 5-6 5"
        }
      />
    </svg>
  );
}

function boardPhase(phase: MatchControllerSnapshot["phase"]): GameBoardPhase {
  if (
    phase === "human-turn" ||
    phase === "bot-thinking" ||
    phase === "committing" ||
    phase === "animating" ||
    phase === "finished"
  ) {
    return phase;
  }
  return "finished";
}

function focusPosition(snapshot: MatchControllerSnapshot) {
  return snapshot.presentation.focusTarget?.kind === "field"
    ? snapshot.presentation.focusTarget.position
    : null;
}

function statusTitle(snapshot: MatchControllerSnapshot): string {
  if (snapshot.mode === "local" && snapshot.game !== null && snapshot.phase === "human-turn") {
    return `${capitalize(snapshot.game.activePlayer)} to move`;
  }
  if (snapshot.phase === "bot-thinking") return "Reading the board";
  if (snapshot.phase === "human-turn") return "Your turn";
  if (snapshot.phase === "finished") return "Final position";
  if (snapshot.phase === "reconciling") return "Checking the saved match";
  return "The table is moving";
}

function announcementText(snapshot: MatchControllerSnapshot): { turn: string; terminal: string } {
  const announcement = snapshot.presentation.announcement;
  if (announcement === null) return { turn: "", terminal: "" };
  if (announcement.kind === "terminal") {
    if (
      announcement.result.kind === "manual-draw" ||
      announcement.result.kind === "repetition-draw"
    ) {
      return {
        turn: "",
        terminal:
          announcement.result.kind === "repetition-draw"
            ? "Match drawn by threefold repetition."
            : "Match ended as a draw.",
      };
    }
    return {
      turn: "",
      terminal: `${capitalize(announcement.result.winner)} wins by ${announcement.result.kind === "passage" ? "passage" : "leaving the opponent with no moves"}.`,
    };
  }
  if (announcement.kind === "selection") {
    return {
      turn: `Selected row ${announcement.position.row + 1}, column ${announcement.position.column + 1}, stack bottom to top ${announcement.stack.join(", ")}. ${announcement.legalTargetCount} legal destinations.`,
      terminal: "",
    };
  }
  if (announcement.kind === "undo") {
    const mover =
      announcement.actor === "human"
        ? "Your"
        : announcement.actor === "bot"
          ? "The bot's"
          : `${capitalize(announcement.actor)}'s`;
    return { turn: `${mover} move was undone.`, terminal: "" };
  }
  return { turn: summarizeTransition(announcement.actor, announcement.events), terminal: "" };
}

function summarizeTransition(
  actor: "human" | "bot" | Player,
  events: readonly TransitionEvent[],
): string {
  const mover = actor === "human" ? "You" : actor === "bot" ? "The bot" : capitalize(actor);
  const moved = events.find((event) => event.kind === "disc-moved" || event.kind === "disc-exited");
  const sourceRemoved = events.some((event) => event.kind === "field-removed");
  const orphan = events.find((event) => event.kind === "orphan-cleanup");
  const action = moved?.kind === "disc-exited" ? "made a winning passage" : "moved a disc";
  const removals =
    (sourceRemoved ? 1 : 0) + (orphan?.kind === "orphan-cleanup" ? orphan.fields.length : 0);
  return `${mover} ${action}.${removals > 0 ? ` ${removals} ${removals === 1 ? "field left" : "fields left"} the board.` : ""}`;
}

function capitalize(player: Player): string {
  return player === "black" ? "Black" : "Red";
}

function LoadingTable() {
  return (
    <section className="loading-table" aria-live="polite">
      <div aria-hidden="true" className="loading-table__disc" />
      <h1>Restoring the table</h1>
      <p>Checking for a saved match on this device.</p>
    </section>
  );
}
